import styled from "@emotion/styled";
import { usePrevious } from "@react-hookz/web";
import { motion } from "@smartsheet/design-tokens";
import { ThemeKey } from "@smartsheet/design-tokens";
import { genId, useForkRef } from "@smartsheet/lodestar-utils";
import { callAllHandlers } from "@smartsheet/lodestar-utils";
import { ThemeMode } from "@smartsheet/sds-theme-internal";
import React, {
    ButtonHTMLAttributes,
    forwardRef,
    MouseEventHandler,
    ReactNode,
    useEffect,
    useMemo,
    useRef,
} from "react";
import { useTheme } from "../../theme/utils/useTheme";
import { createFocusRingStyles } from "../../util/createFocusRingStyles";
import { withFocusToggleClassName } from "../../util/withFocusClassName";
import { TabMenuItem } from "./TabMenuItem";
import { useTabsContext } from "./TabsContext";
import { tabsTheme } from "./tabsTheme";

interface StyledTabProps {
    renderOn: "work-surface" | "canvas";
    selected: boolean;
    themeMode: ThemeMode;
}

export const getTabStyles = (themeMode: ThemeMode, renderOn: "work-surface" | "canvas", selected: boolean) => {
    return `
    background-color: transparent;
    border: none;
    color: ${tabsTheme[renderOn].textColor(themeMode)};
    transition: color ${motion.animation.default};
    font-size: 14px;
    font-weight: ${tabsTheme.font.weight(themeMode)};
    // Override chrome's ua stylesheet which specifies buttons use Arial (wut?).
    font-family: inherit;
    line-height: ${tabsTheme.font.lineHeight(themeMode)};
    margin: 0;
    padding: 0px;
    height: 100%;
    cursor: pointer;

    // handling for added icons
    display: flex;
    align-items: center;
    gap: 4px;

    // This is to ensure these tabs appear on top of the Underlines.
    position: relative;
    z-index: 2;

    :not(:first-of-type) {
        margin-left: ${tabsTheme.tabMarginLeft(themeMode)};
    }

    &:active {
        color: ${tabsTheme[renderOn].activeTextColor(themeMode)};
    }

    ${selected ? `color: ${tabsTheme.selectedTextColor(themeMode)};` : ""}

    ${createFocusRingStyles({
        theme: themeMode,
        componentRadius: tabsTheme.focusRingRadius(themeMode),
        gap: tabsTheme.focusRingGap(themeMode),
        componentBorderWidth: 0,
        secondaryWidth: 2,
    })}

    &:focus::before {
        // Customize the focusRingStyles from above.
        bottom: 0;
        border-bottom-left-radius: ${tabsTheme.focusRingRadiusOverride(themeMode)};
        border-bottom-right-radius: ${tabsTheme.focusRingRadiusOverride(themeMode)};
        // Customize the top padding when focused in Ursa.
        inset-block-start: ${tabsTheme.focusRingInsetOverride(themeMode)};
    }

    &[aria-disabled="true"] {
        color: ${tabsTheme.disabledTextColor(themeMode)};
        cursor: not-allowed;
    }
`;
};

export const StyledTab = withFocusToggleClassName(styled("button")<StyledTabProps>`
    ${({ themeMode, renderOn, selected }) => getTabStyles(themeMode, renderOn, selected)}
`);

StyledTab.displayName = "StyledTab";

export interface TabProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "disabled"> {
    /**
     * The children to render inside this Tab.
     */
    children: ReactNode;
    /**
     * If an id isn't provided, one will be automatically generated.
     * This is used to connect the Tab to its respective TabPanel.
     *
     * As always, this should be unique across the entire DOM at any given point in time.
     */
    id?: string;
    /**
     * Flag to set the disabled state ot the tab.
     */
    isDisabled?: boolean;
    /*
     * Flag to re-register the ref for the tab.
     */
    reRegisterRef?: boolean;
}

export const Tab = forwardRef<HTMLButtonElement, TabProps>(
    ({ children, isDisabled, id, reRegisterRef = false, ...rest }, parentRef) => {
        const { key } = useTheme();
        const themeMode = key as ThemeKey;
        const {
            disableAutoTabFocusWhenSwitching,
            onlyRenderActiveTabPanelChildren: _0,
            onChange,
            renderOn,
            registerTab,
            selectedId,
            manual,
            focusPreviousTab,
            focusNextTab,
            tabRegistrations,
            isCollapsed,
        } = useTabsContext();
        const finalId = useMemo(() => id ?? genId("sds--tab-id"), [id]);
        const localRef = useRef<HTMLButtonElement>(null);
        const ref = useForkRef(localRef, parentRef);
        const isSelected = finalId === selectedId;
        const thisReg = useMemo(
            () => tabRegistrations.find(({ tabId }) => tabId === finalId),
            [finalId, tabRegistrations]
        );

        useEffect(() => {
            const { unregister } = registerTab(finalId, localRef, reRegisterRef);
            return unregister;
        }, [finalId, localRef, registerTab, reRegisterRef]);

        // If the selectedId changes and this tab is the newly selected tab, focus it.
        // This method allows the user to control the selected state of the tab from outside.
        const previousSelectedId = usePrevious(selectedId);
        useEffect(() => {
            if (disableAutoTabFocusWhenSwitching) {
                return;
            }

            if (
                // Note - this should only be run AFTER the initial render, otherwise it could cause
                // focus issues where a tab is focused twice which introduces oddities with the underline.
                previousSelectedId &&
                previousSelectedId !== selectedId &&
                selectedId === finalId &&
                !manual &&
                localRef.current !== document.activeElement
            ) {
                localRef.current?.focus();
            }
        }, [disableAutoTabFocusWhenSwitching, finalId, previousSelectedId, selectedId, manual, thisReg?.tabRef]);

        const onClick: MouseEventHandler<HTMLButtonElement> = callAllHandlers(() => {
            if (!isSelected && !isDisabled) {
                onChange(finalId);
            }
        }, rest.onClick);

        const onKeyDown: React.KeyboardEventHandler<HTMLButtonElement> = callAllHandlers((e) => {
            switch (e.key) {
                case "ArrowLeft":
                    focusPreviousTab();
                    break;
                case "ArrowRight":
                    focusNextTab();
                    break;
            }
        }, rest.onKeyDown);

        return (
            <>
                {!isCollapsed ? (
                    <StyledTab
                        {...rest}
                        id={finalId}
                        ref={ref}
                        selected={isSelected}
                        themeMode={themeMode}
                        onClick={onClick}
                        onKeyDown={onKeyDown}
                        aria-disabled={isDisabled}
                        aria-controls={thisReg?.panelId ?? undefined}
                        aria-selected={isSelected}
                        renderOn={renderOn}
                        tabIndex={isSelected ? 0 : -1}
                        role="tab"
                    >
                        {children}
                    </StyledTab>
                ) : (
                    <TabMenuItem
                        onClick={onClick}
                        selected={isSelected}
                        ref={ref}
                        onKeyDown={onKeyDown}
                        id={finalId}
                        role="tab"
                        isDisabled={isDisabled}
                        {...rest}
                    >
                        {children}
                    </TabMenuItem>
                )}
            </>
        );
    }
);

Tab.displayName = "Tab";
